import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  ArrowsOutIcon,
  ArrowClockwiseIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';

/** A frame belongs to the selected action, never the nearest action with an image. */
export function TraceScreenshot({
  url,
  onRetry,
  fitToViewport = false,
}: {
  url: string;
  fitToViewport?: boolean;
  onRetry?: () => Promise<unknown>;
}) {
  const gt = useGT();
  const [actualSize, setActualSize] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-pressed={actualSize}
          onClick={() => setActualSize(!actualSize)}
        >
          <ArrowsOutIcon className="size-4" />
          {actualSize ? (
            <T context="screenshot zoom control">Fit</T>
          ) : (
            <T context="screenshot zoom control">100%</T>
          )}
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={url} target="_blank" rel="noreferrer">
            <ArrowSquareOutIcon className="size-4" />
            <T context="open screenshot in a new tab">Full Size</T>
          </a>
        </Button>
      </div>
      {failed ? (
        <div role="status" className="flex flex-col items-start gap-2 p-3">
          <T context="trace screenshot loading error">
            Screenshot unavailable. The image may have expired.
          </T>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await onRetry?.();
              setFailed(false);
              setAttempt(attempt + 1);
            }}
          >
            <ArrowClockwiseIcon className="size-4" />
            <T context="retry loading a screenshot">Retry</T>
          </Button>
        </div>
      ) : (
        <div
          className="max-h-[65vh] min-w-0 overflow-auto rounded border"
          tabIndex={0}
          aria-label={gt('Screenshot viewer', {
            context: 'scrollable screenshot region',
          })}
        >
          <img
            key={attempt}
            src={url}
            onError={() => setFailed(true)}
            alt={gt('Selected action screenshot', {
              context: 'trace screenshot image',
            })}
            className={
              actualSize
                ? 'max-w-none'
                : fitToViewport
                  ? 'max-h-[52vh] w-full object-contain'
                  : 'h-auto w-full object-contain'
            }
          />
        </div>
      )}
    </div>
  );
}
